/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useState, useEffect } from 'react';
import { Button, Popover, Modal } from '@arco-design/web-react';
// import { IconMenu } from '@arco-design/web-react/icon';
import { useSelector, useDispatch } from 'react-redux';
// import NetworkIndicator from '@/components/NetworkIndicator';
import LoginModal from '@/components/LoginModal';
import { useIsMobile } from '@/utils/utils';
import { RootState } from '@/store';
import { logout, restoreAuth } from '@/store/slices/auth';
import { 
  localLeaveRoom, 
  clearHistoryMsg, 
  clearCurrentMsg,
  leaveChatPage,
  updateAIGCState,
} from '@/store/slices/room';
import { clearRequestAuth, initRequestInterceptor } from '@/utils/auth';
import RtcClient from '@/lib/RtcClient';
import Logo from '@/assets/img/Logo.svg';
import styles from './index.module.less';


// const Disclaimer = 'https://bwg.zhenyekeji.com/aibuddy/agreement/user.html';
// const ReversoContext = 'https://bwg.zhenyekeji.com/aibuddy/agreement/privacy.html';
// const UserAgreement = 'https://bwg.zhenyekeji.com/aibuddy/agreement/user.html';

interface HeaderProps {
  children?: React.ReactNode;
  hide?: boolean;
}

function Header(props: HeaderProps) {
  const { children, hide } = props;
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const { isAuthenticated, username } = useSelector((state: RootState) => state.auth);
  const { isJoined, isAIGCEnable, scene } = useSelector((state: RootState) => state.room);
  const [loginModalVisible, setLoginModalVisible] = useState(false);

  // 恢复登录状态
  useEffect(() => {
    dispatch(restoreAuth());
    // 恢复请求头中的 API key
    initRequestInterceptor();
  }, [dispatch]);

  // const MenuProps = [
  //   {
  //     name: '免责声明',
  //     url: Disclaimer,
  //   },
  //   {
  //     name: '隐私政策',
  //     url: ReversoContext,
  //   },
  //   {
  //     name: '用户协议',
  //     url: UserAgreement,
  //   },
  // ];

  const handleLogout = async () => {
    Modal.confirm({
      title: '确认退出登录',
      content: '退出登录将结束当前对话并清除所有本地数据，确定要退出吗？',
      okText: '确定退出',
      cancelText: '取消',
      onOk: async () => {
        console.log('========================================');
        console.log('🚪 开始退出登录流程...');

        try {
          // 1. 如果正在 AI 对话中，停止 Agent
          if (isAIGCEnable && scene) {
            console.log('🛑 正在停止 AI Agent...');
            try {
              await RtcClient.stopAgent(scene);
              dispatch(updateAIGCState({ isAIGCEnable: false }));
              console.log('✅ AI Agent 已停止');
            } catch (error) {
              console.warn('⚠️ 停止 AI Agent 失败:', error);
            }
          }

          // 2. 如果已加入房间，离开房间
          if (isJoined) {
            console.log('🚪 正在离开 RTC 房间...');
            try {
              RtcClient.leaveRoom();
              dispatch(localLeaveRoom());
              console.log('✅ 已离开 RTC 房间');
            } catch (error) {
              console.warn('⚠️ 离开房间失败:', error);
            }
          }

          // 3. 清除对话相关数据
          console.log('🧹 正在清除对话数据...');
          dispatch(clearHistoryMsg());
          dispatch(clearCurrentMsg());
          dispatch(leaveChatPage());

          // 4. 清除所有 localStorage 数据
          console.log('🧹 正在清除本地存储...');
          localStorage.clear();
          sessionStorage.clear();

          // 5. 清除 Redux 认证状态
          console.log('🔓 正在清除认证状态...');
          dispatch(logout());

          // 6. 清除请求头中的 Authorization
          clearRequestAuth();

          console.log('✅ 退出登录成功！');
          console.log('========================================');

          // 7. 刷新页面回到主页（强制清理所有状态）
          window.location.href = '/';
        } catch (error) {
          console.error('❌ 退出登录过程中发生错误:', error);
          // 即使出错也要清除认证信息
          localStorage.clear();
          sessionStorage.clear();
          dispatch(logout());
          clearRequestAuth();
          window.location.href = '/';
        }
      },
    });
  };

  return (
    <div
      className={styles.header}
      style={{
        display: hide ? 'none' : 'flex',
      }}
    >
      <div className={styles['header-logo']}>
        {/* {useIsMobile() ? null : (
          <Popover
            content={
              <div className={styles['menu-wrapper']}>
                {MenuProps.map((menuItem) => (
                  <Button
                    type="text"
                    key={menuItem.name}
                    onClick={() => {
                      window.open(menuItem.url, '_blank');
                    }}
                  >
                    {menuItem.name}
                  </Button>
                ))}
              </div>
            }
          >
            <IconMenu className={styles['header-setting-btn']} />
          </Popover>
        )} */}
        <img src={Logo} alt="Logo" />
        {/* <Divider type="vertical" /> */}
        {/* <span className={styles['header-logo-text']}>AbsoluteMind</span> */}
        {/* <NetworkIndicator /> */}
      </div>
      {children}
      <div className={styles['header-right']}>
        {isAuthenticated ? (
          isMobile ? (
            // 移动端：点击用户名直接退出登录
            <div
              className={styles['header-right-text']}
              onClick={handleLogout}
            >
              {username}
            </div>
          ) : (
            // PC端：悬浮显示退出登录按钮
            <Popover
              content={
                <div className={styles['menu-wrapper']}>
                  <Button
                    type="text"
                    onClick={handleLogout}
                  >
                    退出登录
                  </Button>
                </div>
              }
            >
              <div className={styles['header-right-text']}>
                {username}
              </div>
            </Popover>
          )
        ) : (
          <div
            className={styles['header-right-text']}
            onClick={() => setLoginModalVisible(true)}
          >
            登录
          </div>
        )}
      </div>
      <LoginModal
        visible={loginModalVisible}
        onClose={() => setLoginModalVisible(false)}
      />
    </div>
  );
}

export default Header;
